import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { EVENT_TYPES, type EventType } from '../types';

/**
 * Formatting utilities for display
 */

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp: string): string {
  try {
    return format(parseISO(timestamp), 'MMM d, yyyy HH:mm:ss');
  } catch {
    return timestamp;
  }
}

/**
 * Format timestamp as relative time
 */
export function formatRelativeTime(timestamp: string): string {
  try {
    return formatDistanceToNow(parseISO(timestamp), { addSuffix: true });
  } catch {
    return timestamp;
  }
}

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null) return '-';
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / Math.pow(1024, exponent);

  return `${value.toFixed(exponent > 0 ? 1 : 0)} ${units[exponent]}`;
}

/**
 * Format duration in human-readable format
 */
export function formatDuration(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

/**
 * Get display label for event type
 */
export function getEventTypeLabel(eventType: EventType): string {
  const labels: Record<EventType, string> = {
    [EVENT_TYPES.DEVICE_LINK_CONFLICT]: 'Device Link Conflict',
    [EVENT_TYPES.DEVICE_LINK_CLAIMED]: 'Device Link Claimed',
    [EVENT_TYPES.DEVICE_AUTHENTICATED]: 'Device Authenticated',
    [EVENT_TYPES.SYNC_HANDSHAKE]: 'Sync Handshake',
    [EVENT_TYPES.FILE_UPLOAD]: 'File Upload',
    [EVENT_TYPES.FILE_DOWNLOAD]: 'File Download',
    [EVENT_TYPES.CONFLICT_CREATED]: 'Conflict Created',
    [EVENT_TYPES.CONFLICT_RESOLVED]: 'Conflict Resolved',
    [EVENT_TYPES.ERROR]: 'Error',
  };
  return labels[eventType] || eventType;
}

/**
 * Get icon name/emoji for event type
 */
export function getEventTypeIcon(eventType: EventType): string {
  const icons: Record<EventType, string> = {
    [EVENT_TYPES.DEVICE_LINK_CONFLICT]: '⚠️',
    [EVENT_TYPES.DEVICE_LINK_CLAIMED]: '🔗',
    [EVENT_TYPES.DEVICE_AUTHENTICATED]: '🔐',
    [EVENT_TYPES.SYNC_HANDSHAKE]: '🤝',
    [EVENT_TYPES.FILE_UPLOAD]: '⬆️',
    [EVENT_TYPES.FILE_DOWNLOAD]: '⬇️',
    [EVENT_TYPES.CONFLICT_CREATED]: '⚡',
    [EVENT_TYPES.CONFLICT_RESOLVED]: '✅',
    [EVENT_TYPES.ERROR]: '❌',
  };
  return icons[eventType] || '📄';
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string | null, maxLength: number): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Format UUID for display (shortened)
 */
export function formatUUID(uuid: string | null): string {
  if (!uuid) return '-';
  return uuid.slice(0, 8);
}

